import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Card, FlowGrid, BillboardText } from '@fs/zion-ui'
import { getUser } from '@fs/zion-user'
import { useSlsStateContext } from '@fs/zion-sls'
import { FullTextApp } from '../../../../shared/services/config'
import RelationshipsCardEditMode from './RelationshipsCardEditMode'
import RelationshipsReviewList from './components/RelationshipsReviewList'
import RelationshipsHeader from './components/RelationshipsHeader'
import { DISABLED_TEXT_COLOR } from '../shared/utils/panelConstants'
import { getPersonsAndRelationshipsForHousehold } from '../shared/relationships/relationshipQueries'
import { getNoRelationLabel } from './utils/relationship.utils'
import useRecords from '../../../views/indexing/hooks/useRecords'
import { useSharedBrowserStateContext } from '../../../../shared/providers/sharedBrowserProvider/SharedBrowserContext'

/**
 * Lists the other people in a person's group by their relationship to that person, with an inline
 * edit mode for adding, removing and relating members.
 *
 * Rendered even for a group with no other people in it — that is the only route into a one-person
 * group. When the person is in no group at all there is nothing to edit, so the Edit affordance is
 * disabled and the card explains how to put them in one.
 *
 * @component
 * @param {Object}   props
 * @param {Object}   props.person               SLS person object used to look up relationships (must contain at least id / recordId).
 * @param {Function} [props.onSave]             Callback fired after a successful save (receives form data from edit mode).
 * @param {boolean}  [props.reviewed=false]     Whether the card is in pending mode (not reviewed) or reviewed.
 * @param {Function} [props.setReviewed]        Setter to update reviewed state (used in edit mode).
 * @param {boolean}  [props.editMode=false]     Forces the card into edit mode (wizard / step-based flows).
 * @param {Function} [props.setEditMode]        Setter to update edit mode state.
 * @param {boolean}  [props.isInReviewProcess=false] Indicates if the parent panel is in review process mode for newly added people.
 * @param {boolean}  [props.saveCardDataToSls=false]        Whether to save the card data to SLS upon submission.
 * @param {string}   [props.splitScreenPartitionKey]        Split screen partition key if using split screen.
 * @param {Function} [props.onDirtyChange=(isDirty) => {}] - Callback function invoked when the form dirty state changes.
 * @param {string}   [props.appName=FullTextApp] The application name for SLS operations.
 * @param {boolean}  [props.isCreatingNewPerson=false] Whether the card is being used in the context of creating a new person (vs editing an existing one).
 * @param {boolean}  [props.disableEdit=false] Disables editing (e.g. a restricted field).
 * @param {boolean}  [props.disableGroupChanges=false] Hides the affordances that grow or shrink the group,
 *                                                     for callers the patron already reached through one.
 * @param {Function} [props.onAddExistingPerson] Handler for "Add existing person".
 * @param {Function} [props.onAddNewPerson]      Handler for "Add new person".
 *
 * @returns {JSX.Element}
 */
export const RelationshipsCard = ({
  person,
  onSave,
  reviewed = false,
  setReviewed,
  editMode = false,
  setEditMode,
  isInReviewProcess = false,
  onDirtyChange = () => {},
  saveCardDataToSls = false,
  splitScreenPartitionKey,
  appName = FullTextApp,
  isCreatingNewPerson = false,
  disableEdit = false,
  disableGroupChanges = false,
  onAddExistingPerson,
  onAddNewPerson,
}) => {
  const { images, currentImageArk } = useSlsStateContext()
  const { thumbnails } = useSharedBrowserStateContext()
  const [userId, setUserId] = useState(null)

  const { allRecords } = useRecords({
    images,
    currentImageArk,
    thumbnails,
  })

  // Determine the recordId to use: prefer person.recordId, else look for a superElement starting with '1:2'
  const recordId = person?.recordId || person?.superElements?.find((se) => se?.id?.startsWith('1:2'))?.id || null

  // No record at all means no group, unlike a one-person group: neither group-level alert applies.
  const { relationships, record } = getPersonsAndRelationshipsForHousehold({
    person,
    images,
    allRecords,
    recordId,
    currentPartitionKey: currentImageArk,
    noRelationLabel: getNoRelationLabel,
  })

  const noGroup = !record

  useEffect(() => {
    getUser().then((u) => setUserId(u.cisId))
  }, [])

  if (!onSave) {
    console.error('ReviewCard: onSave prop is required but was not provided')
    return null
  }

  return (
    <Card margins="xxs" edges={editMode ? 'elevation' : 'border'}>
      <BillboardText primaryColor={!reviewed && !editMode ? DISABLED_TEXT_COLOR : undefined}>
        <FlowGrid orientation="vertical" columnCount={1} gutters={editMode ? 'xs' : 'xxs'}>
          <RelationshipsHeader
            setIsEditing={setEditMode}
            isEditing={editMode}
            reviewed={reviewed}
            disableEdit={disableEdit}
            noGroup={noGroup}
          />
          {(!editMode || noGroup) && (
            <RelationshipsReviewList
              person={person}
              relationships={relationships}
              reviewed={reviewed}
              isInReviewProcess={isInReviewProcess}
              noGroup={noGroup}
            />
          )}
          {editMode && !noGroup && (
            <RelationshipsCardEditMode
              person={person}
              allRecords={allRecords}
              record={record}
              relationships={relationships}
              reviewed={reviewed}
              setReviewed={setReviewed}
              setIsEditing={setEditMode}
              userId={userId}
              onSave={onSave}
              onDirtyChange={onDirtyChange}
              saveCardDataToSls={saveCardDataToSls}
              splitScreenPartitionKey={splitScreenPartitionKey}
              appName={appName}
              isCreatingNewPerson={isCreatingNewPerson}
              disableGroupChanges={disableGroupChanges}
              onAddExistingPerson={onAddExistingPerson}
              onAddNewPerson={onAddNewPerson}
            />
          )}
        </FlowGrid>
      </BillboardText>
    </Card>
  )
}

RelationshipsCard.propTypes = {
  person: PropTypes.shape({
    // Required properties
    id: PropTypes.string.isRequired,
    // recordId can be null for floating persons (persons not yet attached to a record)
    recordId: PropTypes.string,
  }).isRequired,
  onSave: PropTypes.func.isRequired,
  reviewed: PropTypes.bool,
  setReviewed: PropTypes.func,
  editMode: PropTypes.bool,
  setEditMode: PropTypes.func,
  isInReviewProcess: PropTypes.bool,
  onDirtyChange: PropTypes.func,
  saveCardDataToSls: PropTypes.bool,
  splitScreenPartitionKey: PropTypes.string,
  appName: PropTypes.string,
  isCreatingNewPerson: PropTypes.bool,
  disableEdit: PropTypes.bool,
  disableGroupChanges: PropTypes.bool,
  onAddExistingPerson: PropTypes.func,
  onAddNewPerson: PropTypes.func,
}

RelationshipsCard.defaultProps = {
  reviewed: false,
  editMode: false,
  setEditMode: undefined,
  isCreatingNewPerson: false,
}
